import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Partners from "./pages/Partners";
import Ratecards from "./pages/Ratecards";
import Catalog from "./pages/Catalog";
import WebsiteControl from "./pages/WebsiteControl";
import Jobs from "./pages/Jobs";
import CollectPayment from "./pages/CollectPayment";
import EstimateCalculatorAdmin from "./pages/EstimateCalculatorAdmin";
import Leads from "./pages/Leads";
import Documents from "./pages/Documents";
import Feedback from "./pages/Feedback";
import Competition from "./pages/Competition";
import Serviceability from "./pages/admin/Serviceability";
import Cities from "./pages/admin/Cities";
import CityPage from "./pages/store/CityPage";
import SellDevices from "./pages/store/SellDevices";
import SellOffer from "./pages/store/SellOffer";
import SellDevicesAdmin from "./pages/admin/SellDevicesAdmin";
import MediaRequirements from "./pages/admin/MediaRequirements";
import PartnerEngineers from "./pages/partner/PartnerEngineers";
import Settings from "./pages/Settings";
import Permissions from "./pages/Permissions";
import Users from "./pages/Users";
import { PartnerLayout } from "./components/PartnerLayout";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerJobs from "./pages/partner/PartnerJobs";
import PartnerSellPickups from "./pages/partner/PartnerSellPickups";
import PartnerSops from "./pages/partner/PartnerSops";
import Sops from "./pages/Sops";
import PartnerDocuments from "./pages/partner/PartnerDocuments";
import PartnerRatecards from "./pages/partner/PartnerRatecards";
import PartnerNotifications from "./pages/partner/PartnerNotifications";
import PartnerProfile from "./pages/partner/PartnerProfile";
import { CustomerLayout } from "./components/CustomerLayout";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerRequests from "./pages/customer/CustomerRequests";
import CustomerNewRequest from "./pages/customer/CustomerNewRequest";
import CustomerInvoices from "./pages/customer/CustomerInvoices";
import CustomerProfile from "./pages/customer/CustomerProfile";
import Customers from "./pages/Customers";
import Accounting from "./pages/Accounting";
import Reports from "./pages/Reports";
import AuditLogs from "./pages/AuditLogs";
import PartnerApplications from "./pages/PartnerApplications";
import { CartProvider } from "./context/CartContext";
import { LocationProvider } from "./context/LocationContext";
import Home from "./pages/store/Home";
import Services from "./pages/store/Services";
import Cart from "./pages/store/Cart";
import Checkout from "./pages/store/Checkout";
import BookingConfirmed from "./pages/store/BookingConfirmed";
import BecomePartner from "./pages/store/BecomePartner";
import BookService from "./pages/store/BookService";
import BookServicePublic from "./pages/store/BookServicePublic";
import Terms from "./pages/store/Terms";
import About from "./pages/store/About";
import CustomerTerms from "./pages/store/CustomerTerms";
import Privacy from "./pages/store/Privacy";
import EstimateCalculator from "./pages/store/EstimateCalculator";
import SchedulePage from "./pages/store/SchedulePage";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "partner") return <Navigate to="/portal" replace />;
  if (user.role === "customer") return <Navigate to="/account" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  return <Layout>{children}</Layout>;
}

function PartnerProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "partner") return <Navigate to="/" replace />;
  return <PartnerLayout>{children}</PartnerLayout>;
}

function CustomerProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "customer") return <Navigate to="/" replace />;
  return <CustomerLayout>{children}</CustomerLayout>;
}

function ImpersonationBanner() {
  const { user, impersonating, stopImpersonating } = useAuth();
  if (!impersonating || !user) return null;
  return (
    <div data-testid="impersonation-banner" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 bg-slate-900 text-white rounded-full shadow-lg px-5 py-2.5 text-sm">
      <span>👁️ Viewing as <strong>{user.name}</strong> ({user.role})</span>
      <button onClick={stopImpersonating} data-testid="stop-impersonating-btn" className="bg-white text-slate-900 font-semibold rounded-full px-3 py-1 text-xs hover:bg-slate-100 transition-colors">
        Return to Admin
      </button>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
          <LocationProvider>
          <ImpersonationBanner />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/booking-confirmed" element={<BookingConfirmed />} />
            <Route path="/become-partner" element={<BecomePartner />} />
            <Route path="/book" element={<BookService />} />
            <Route path="/book-service" element={<BookServicePublic />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/customer-terms" element={<CustomerTerms />} />
            <Route path="/city/:slug" element={<CityPage />} />
            <Route path="/sell" element={<SellDevices />} />
            <Route path="/sell/offer/:bookingId" element={<SellOffer />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/estimate" element={<EstimateCalculator />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/admin" element={<Protected roles={["admin", "ops", "finance"]}><Dashboard /></Protected>} />
            <Route path="/partners" element={<Protected roles={["admin", "ops"]}><Partners /></Protected>} />
            <Route path="/applications" element={<Protected roles={["admin", "ops"]}><PartnerApplications /></Protected>} />
            <Route path="/jobs" element={<Protected roles={["admin", "ops", "finance"]}><Jobs /></Protected>} />
            <Route path="/collect-payment" element={<Protected roles={["admin", "ops", "finance"]}><CollectPayment /></Protected>} />
            <Route path="/estimate-calculator" element={<Protected roles={["admin", "ops"]}><EstimateCalculatorAdmin /></Protected>} />
            <Route path="/leads" element={<Protected roles={["admin", "ops", "finance"]}><Leads /></Protected>} />
            <Route path="/ratecards" element={<Protected roles={["admin", "ops"]}><Ratecards /></Protected>} />
            <Route path="/documents" element={<Protected roles={["admin", "ops"]}><Documents /></Protected>} />
            <Route path="/feedback" element={<Protected roles={["admin", "ops", "finance"]}><Feedback /></Protected>} />
            <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
            <Route path="/permissions" element={<Protected roles={["admin"]}><Permissions /></Protected>} />
            <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
            <Route path="/catalog" element={<Protected roles={["admin"]}><Catalog /></Protected>} />
            <Route path="/sops" element={<Protected roles={["admin", "ops"]}><Sops /></Protected>} />
            <Route path="/website" element={<Protected roles={["admin"]}><WebsiteControl /></Protected>} />
            <Route path="/customers" element={<Protected roles={["admin", "ops", "finance"]}><Customers /></Protected>} />
            <Route path="/competition" element={<Protected roles={["admin", "ops"]}><Competition /></Protected>} />
            <Route path="/serviceability" element={<Protected roles={["admin", "ops"]}><Serviceability /></Protected>} />
            <Route path="/cities" element={<Protected roles={["admin", "ops"]}><Cities /></Protected>} />
            <Route path="/sell-devices" element={<Protected roles={["admin", "ops"]}><SellDevicesAdmin /></Protected>} />
            <Route path="/media-requirements" element={<Protected roles={["admin", "ops"]}><MediaRequirements /></Protected>} />
            <Route path="/accounting" element={<Protected roles={["admin", "finance"]}><Accounting /></Protected>} />
            <Route path="/reports" element={<Protected roles={["admin", "finance"]}><Reports /></Protected>} />
            <Route path="/audit" element={<Protected roles={["admin", "finance"]}><AuditLogs /></Protected>} />

            <Route path="/portal" element={<PartnerProtected><PartnerDashboard /></PartnerProtected>} />
            <Route path="/portal/jobs" element={<PartnerProtected><PartnerJobs /></PartnerProtected>} />
            <Route path="/portal/sell-pickups" element={<PartnerProtected><PartnerSellPickups /></PartnerProtected>} />
            <Route path="/portal/sops" element={<PartnerProtected><PartnerSops /></PartnerProtected>} />
            <Route path="/portal/documents" element={<PartnerProtected><PartnerDocuments /></PartnerProtected>} />
            <Route path="/portal/ratecards" element={<PartnerProtected><PartnerRatecards /></PartnerProtected>} />
            <Route path="/portal/notifications" element={<PartnerProtected><PartnerNotifications /></PartnerProtected>} />
            <Route path="/portal/profile" element={<PartnerProtected><PartnerProfile /></PartnerProtected>} />
            <Route path="/portal/engineers" element={<PartnerProtected><PartnerEngineers /></PartnerProtected>} />

            <Route path="/account" element={<CustomerProtected><CustomerDashboard /></CustomerProtected>} />
            <Route path="/account/requests" element={<CustomerProtected><CustomerRequests /></CustomerProtected>} />
            <Route path="/account/new" element={<CustomerProtected><CustomerNewRequest /></CustomerProtected>} />
            <Route path="/account/invoices" element={<CustomerProtected><CustomerInvoices /></CustomerProtected>} />
            <Route path="/account/profile" element={<CustomerProtected><CustomerProfile /></CustomerProtected>} />
          </Routes>
          </LocationProvider>
          </CartProvider>
        </AuthProvider>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
